/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, inject, OnInit, signal, OnDestroy } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { VaultAuthService, VaultStorageService, captureVaultSessionForExpiryLogout } from '@nebula/shared-vault/data-access';
import { LoaderComponent } from '@nebula-shared/ui';
import { Subject, takeUntil, timer } from 'rxjs';
@Component({
  imports: [RouterModule, CommonModule, LoaderComponent],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
  standalone: true,
})
export class App implements OnInit, OnDestroy {
  private readonly vaultStorage = inject(VaultStorageService);
  private readonly vaultAuthService = inject(VaultAuthService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();
  showLoader = signal<boolean>(true);
  isAuthenticated = false;
  hasMultipleModules = signal<boolean>(false);
  tokenExpiry = signal<number | null>(null);
  tokenRemainingTime = signal<number | null>(null);
  ngOnInit(): void {
    this.showLoader.set(true);
    this.hasMultipleModules.set(this.vaultStorage.getHasMultipleModules());
    this.tokenExpiry.set(this.vaultStorage.getTokenExpiry());
    if (this.tokenExpiry()) {
      const remaining = this.vaultStorage.getTokenRemainingTime();
      this.tokenRemainingTime.set(remaining);
    }
    this.isAuthenticated = this.vaultAuthService.isAuthenticated();
    if (this.isAuthenticated) {
      this.redirectIfNeeded();
    }
    this.vaultStorage.storage$.pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      this.applyTokensViaAuthService(data);
      if (data.hasMultipleModules !== undefined) {
        this.hasMultipleModules.set(data.hasMultipleModules === 'true');
      }
      if (data.tokenExpiry) {
        this.tokenExpiry.set(parseInt(data.tokenExpiry, 10));
        const expiry = this.tokenExpiry();
        if (expiry !== null) {
          const remaining = expiry - Date.now();
          this.tokenRemainingTime.set(remaining > 0 ? remaining : 0);
        }
      }
    });
    this.loadInitialData();
    this.startTokenExpiryCheck();
  }
  private loadInitialData(): void {
    const cookieData = this.vaultStorage.getDataFromCookie();
    if (cookieData) {
      this.isAuthenticated = this.vaultAuthService.isAuthenticated();
      if (this.isAuthenticated) {
        this.redirectIfNeeded();
      }
    }
  }
  private applyTokensViaAuthService(tokens: any): void {
    const accessToken = tokens?.accessToken || tokens?.access_token;
    if (!accessToken) {
      return;
    }
    setTimeout(() => {
      const isAuth = this.vaultAuthService.isAuthenticated();
      this.isAuthenticated = isAuth;
      if (isAuth) {
        this.redirectIfNeeded();
      }
      this.showLoader.set(false);
    }, 100);
  }
  private redirectIfNeeded(): void {
    const currentUrl = this.router.url.split('?')[0];
    if (currentUrl === '/login' || currentUrl.endsWith('/login')) {
      this.showLoader.set(false);
      return;
    }
    if (currentUrl === '/' || currentUrl === '') {
      if (this.hasMultipleModules()) {
        this.router.navigate(['/layout/products']);
      } else {
        this.router.navigate(['/layout']);
      }
    }
  }
  private startTokenExpiryCheck(): void {
    timer(0, 60000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const isValid = this.vaultStorage.isTokenValid();
        if (!isValid && this.isAuthenticated) {
          captureVaultSessionForExpiryLogout();
          this.vaultAuthService.logout();
        }
      });
  }
  onLoaderComplete() {
    this.showLoader.set(false);
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
